/**
 * ProgramMetricsTab — Cross-cohort metrics for program owners and admins.
 *
 * Shows:
 *   1. At a Glance — program-level totals (cohorts, participants, graduates, rate)
 *   2. Per-cohort rows sorted newest first, each showing:
 *        - Cohort name + status badge + date range
 *        - Enrolled / Graduated / Graduation Rate stat counters
 *        - Two-segment stacked bar: graduated (green) vs. enrolled-but-not-graduated (blue)
 *
 * All data comes from the already-fetched cohorts list — no extra API calls.
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import Badge from '@cloudscape-design/components/badge';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Tooltip from '@cloudscape-design/components/tooltip';
import { Cohort } from '../../api/cohorts';
import { StatCounter } from '../../components/StatCounter';

// ── Bar segment with tooltip on hover ────────────────────────────────────────

interface BarSegmentProps {
  pct: number;
  color: string;
  label: string;
  count: number;
}

function BarSegment({ pct, color, label, count }: BarSegmentProps) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = React.useState(false);
  if (pct <= 0) return null;
  return (
    <span
      ref={ref}
      onPointerEnter={() => setVisible(true)}
      onPointerLeave={() => setVisible(false)}
      style={{
        width: `${pct}%`,
        height: '100%',
        backgroundColor: color,
        transition: 'width 0.3s',
        display: 'block',
      }}
    >
      {visible && (
        <Tooltip
          content={`${label}: ${count} (${pct}%)`}
          getTrack={() => ref.current}
          onEscape={() => setVisible(false)}
          position="top"
        />
      )}
    </span>
  );
}

// ── Two-segment distribution bar ─────────────────────────────────────────────

interface MiniBarProps {
  graduated: number;
  enrolled: number;
}

function MiniBar({ graduated, enrolled }: MiniBarProps) {
  if (enrolled === 0) return <Box color="text-body-secondary" fontSize="body-s">No enrolled participants</Box>;

  const pctGraduated  = Math.round((graduated / enrolled) * 100);
  const pctInProgress = Math.max(0, 100 - pctGraduated);
  const inProgress    = enrolled - graduated;

  return (
    <SpaceBetween size="xs">
      <div style={{ display: 'flex', height: '20px', borderRadius: '4px', overflow: 'hidden', width: '100%' }}>
        <BarSegment pct={pctGraduated}  color="#1d8102" label="Graduated"     count={graduated}  />
        <BarSegment pct={pctInProgress} color="#d5dbdb" label="Not graduated" count={inProgress} />
      </div>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        {[
          { color: '#1d8102', label: 'Graduated',     count: graduated,  pct: pctGraduated  },
          { color: '#d5dbdb', label: 'Not graduated', count: inProgress, pct: pctInProgress },
        ].map(s => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: s.color, flexShrink: 0 }} />
            <Box fontSize="body-s">{s.label}: <strong>{s.count}</strong> ({s.pct}%)</Box>
          </div>
        ))}
      </div>
    </SpaceBetween>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

function CohortStatusBadge({ status }: { status: Cohort['status'] }) {
  switch (status) {
    case 'active':             return <Badge color="green">Active</Badge>;
    case 'registration-open': return <Badge color="blue">Registration Open</Badge>;
    case 'upcoming':          return <Badge color="green">Upcoming</Badge>;
    case 'completed':         return <Badge color="grey">Completed</Badge>;
    default:                  return null;
  }
}

// ── Date formatting ───────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// ── Per-cohort row ────────────────────────────────────────────────────────────

interface CohortMetricsRowProps {
  cohort: Cohort;
}

function CohortMetricsRow({ cohort }: CohortMetricsRowProps) {
  const navigate = useNavigate();
  const enrolled  = cohort.enrollmentCount ?? 0;
  const graduated = cohort.graduatedCount  ?? 0;
  const rate      = enrolled > 0 ? Math.round((graduated / enrolled) * 100) : 0;

  return (
    <Container
      header={
        <Header
          variant="h3"
          actions={
            <Button
              variant="inline-link"
              onClick={() => navigate(`/cohorts/${cohort.cohortId}#metrics`)}
            >
              View Cohort Metrics
            </Button>
          }
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span>{cohort.cohortName}</span>
            <CohortStatusBadge status={cohort.status} />
            <Box color="text-body-secondary" fontSize="body-s" display="inline">
              {formatDate(cohort.startDate)} – {formatDate(cohort.endDate)}
            </Box>
          </div>
        </Header>
      }
    >
      <SpaceBetween size="m">
        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
          <StatCounter label="Enrolled"        value={enrolled}    />
          <StatCounter label="Graduated"       value={graduated}   />
          <StatCounter label="Graduation Rate" value={`${rate}%`}  />
        </div>
        <MiniBar graduated={graduated} enrolled={enrolled} />
      </SpaceBetween>
    </Container>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface ProgramMetricsTabProps {
  cohorts: Cohort[];
}

export function ProgramMetricsTab({ cohorts }: ProgramMetricsTabProps) {
  // Newest first by start date
  const sortedCohorts = [...cohorts].sort(
    (a, b) => b.startDate.localeCompare(a.startDate),
  );

  const totalParticipants = cohorts.reduce((sum, c) => sum + (c.enrollmentCount ?? 0), 0);
  const totalGraduated    = cohorts.reduce((sum, c) => sum + (c.graduatedCount  ?? 0), 0);
  const graduationRate    = totalParticipants > 0 ? Math.round((totalGraduated / totalParticipants) * 100) : 0;

  return (
    <SpaceBetween size="l">

      {/* ── At a Glance ──────────────────────────────────────────────────── */}
      <Container header={<Header variant="h2">At a Glance</Header>}>
        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
          <StatCounter label="Total Cohorts"      value={cohorts.length}    />
          <StatCounter label="Total Participants" value={totalParticipants} />
          <StatCounter label="Total Graduates"    value={totalGraduated}    />
          <StatCounter label="Graduation Rate"    value={`${graduationRate}%`} />
        </div>
      </Container>

      {/* ── Per-cohort breakdown ─────────────────────────────────────────── */}
      {sortedCohorts.length === 0 ? (
        <Container>
          <Box color="text-body-secondary" textAlign="center" padding={{ vertical: 'l' }}>
            No cohorts have been created for this program yet.
          </Box>
        </Container>
      ) : (
        sortedCohorts.map(cohort => (
          <CohortMetricsRow key={cohort.cohortId} cohort={cohort} />
        ))
      )}

    </SpaceBetween>
  );
}
